import React, { useCallback, useEffect, useState } from "react";
import {
    Button,
    Card,
    Space,
    Typography,
    Breadcrumb,
    Row,
    Col,
    Popconfirm,
} from "antd";
import {
    DeleteOutlined,
    HomeOutlined,
    PlusOutlined,
    EditOutlined,
} from "@ant-design/icons";
import { Spinner } from "reactstrap";
import DataTable from "../../components/CustomDatatable/index.js";
import moment from "moment";
import { stringSorter } from "../../utils/functions.js";
import { useDispatch, useSelector } from "react-redux";
import { getItems, deleteItem, updateItem, addItem } from "../../store/actions/item.js";
import { getCustomers } from "../../store/actions/customers";
import { getPackage } from "../../store/actions/package";
import { getPackageTransaction } from "../../store/actions/packageTransactions.js";
import { getSeries } from "../../store/actions/series.js";
import { getCategories } from "../../store/actions/category.js";
import { getColors } from "../../store/actions/color.js";
import { enhanceColumnsWithFilters } from "../../components/ColumnFilters/index.js";
import ItemForm from "./ItemForm.js";

const { Title } = Typography;

const Items = () => {
    document.title = "Items";
    const [isLoading, setIsLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const dispatch = useDispatch();

    const items = useSelector((state) => state?.item?.list ?? []);
    const seriesList = useSelector((state) => state?.series?.list ?? []);
    const categories = useSelector((state) => state?.category?.list ?? []);
    const colors = useSelector((state) => state?.color?.list ?? []);

    // Quick lookup maps for displaying names in the table
    const seriesMap = seriesList.reduce((acc, s) => { acc[s.id] = s.name; return acc; }, {});
    const categoryMap = categories.reduce((acc, c) => { acc[c.id] = c.name; return acc; }, {});
    const colorMap = colors.reduce((acc, c) => { acc[c.id] = c.name; return acc; }, {});

    // ── Fetch items (used after add/update/delete) ────────────────────────────
    const getData = useCallback(async () => {
        setIsLoading(true);
        await dispatch(getItems());
        setIsLoading(false);
    }, [dispatch]);

    // ── Fetch all reference data on mount ─────────────────────────────────────
    useEffect(() => {
        getData();
        dispatch(getCustomers());
        dispatch(getPackage());
        dispatch(getPackageTransaction());
        dispatch(getSeries());
        dispatch(getCategories());
        dispatch(getColors());
    }, [dispatch, getData]);

    // ─── ADD / UPDATE ─────────────────────────────────────────────────────────
    const handleCreate = useCallback(async (values) => {
        if (selectedRecord?.id) {
            const res = await dispatch(updateItem({
                id: selectedRecord.id,
                ...values,
                // send IDs directly — backend expects numbers, dropdowns give _id strings
                seriesId: values.seriesId ? values.seriesId : undefined,
                categoryid: values.categoryid ? values.categoryid : undefined,
                colorId: values.colorId ? values.colorId : undefined,
            }));
            if (res?.code === 1) getData();
        } else {
            const res = await dispatch(addItem(values));
            if (res?.code === 1) getData();
        }
        setIsModalVisible(false);
        setSelectedRecord(null);
    }, [dispatch, selectedRecord?.id, getData]);

    // ─── DELETE ───────────────────────────────────────────────────────────────
    const handleDelete = useCallback(async (id) => {
        const res = await dispatch(deleteItem(id));
        if (res?.code === 1) getData();
    }, [dispatch, getData]);

    // ─── Open edit ────────────────────────────────────────────────────────────
    const handleEdit = (record) => {
        setSelectedRecord(record);
        setIsModalVisible(true);
    };

    const renderCell = (text) => (text ? text : "N/A");

    let columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: stringSorter("name"),
            sortDirections: ["ascend", "descend"],
        },
        {
            title: "Series",
            dataIndex: "seriesId",
            key: "seriesId",
            sorter: stringSorter("seriesId"),
            sortDirections: ["ascend", "descend"],
            render: (id) => seriesMap[id] || renderCell(null),
        },
        {
            title: "Category",
            dataIndex: "categoryid",
            key: "categoryid",
            sorter: stringSorter("categoryid"),
            sortDirections: ["ascend", "descend"],
            render: (id) => categoryMap[id] || renderCell(null),
        },
        {
            title: "Color",
            dataIndex: "colorId",
            key: "colorId",
            sorter: stringSorter("colorId"),
            sortDirections: ["ascend", "descend"],
            render: (id) => colorMap[id] || renderCell(null),
        },
        {
            title: "Front Camera",
            dataIndex: "frontCamera",
            key: "frontCamera",
            sorter: stringSorter("frontCamera"),
            sortDirections: ["ascend", "descend"],
            render: renderCell,
        },
        {
            title: "Rear Camera",
            dataIndex: "rearCamera",
            key: "rearCamera",
            sorter: stringSorter("rearCamera"),
            sortDirections: ["ascend", "descend"],
            render: renderCell,
        },
        {
            title: "Processor",
            dataIndex: "processor",
            key: "processor",
            sorter: stringSorter("processor"),
            sortDirections: ["ascend", "descend"],
            render: renderCell,
        },
        {
            title: "Weight",
            dataIndex: "weight",
            key: "weight",
            sorter: stringSorter("weight"),
            sortDirections: ["ascend", "descend"],
            render: renderCell,
        },
        {
            title: "Length",
            dataIndex: "length",
            key: "length",
            sorter: stringSorter("length"),
            sortDirections: ["ascend", "descend"],
            render: renderCell,
        },
        {
            title: "Height",
            dataIndex: "height",
            key: "height",
            sorter: stringSorter("height"),
            sortDirections: ["ascend", "descend"],
            render: renderCell,
        },
        {
            title: "Display",
            dataIndex: "display",
            key: "display",
            sorter: stringSorter("display"),
            sortDirections: ["ascend", "descend"],
            render: renderCell,
        },
        {
            title: "MM Size",
            dataIndex: "mmSize",
            key: "mmSize",
            sorter: stringSorter("mmSize"),
            sortDirections: ["ascend", "descend"],
            render: renderCell,
        },
        {
            title: "Battery",
            dataIndex: "battery",
            key: "battery",
            sorter: stringSorter("battery"),
            sortDirections: ["ascend", "descend"],
            render: renderCell,
        },
        {
            title: "Display Type",
            dataIndex: "displayType",
            key: "displayType",
            sorter: stringSorter("displayType"),
            sortDirections: ["ascend", "descend"],
            render: renderCell,
        },
        {
            title: "Action",
            key: "action",
            fixed: "right",
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        title="Edit"
                    />
                    <Popconfirm
                        title="Are you sure you want to delete this record?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            title="Delete"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    columns = enhanceColumnsWithFilters(columns);

    return (
        <div className="page-content">
            <Breadcrumb style={{ marginBottom: "16px" }}>
                <Breadcrumb.Item href="/"><HomeOutlined /></Breadcrumb.Item>
                <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
                <Breadcrumb.Item>Items</Breadcrumb.Item>
            </Breadcrumb>

            <Card>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={2} style={{ margin: 0 }}>Items</Title>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => { setSelectedRecord(null); setIsModalVisible(true); }}
                            >
                                Add Item
                            </Button>
                        </Col>
                    </Row>

                    {isLoading ? (
                        <div className="d-flex justify-content-center">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <DataTable
                            dataSource={items.map((item) => ({ ...item, key: item.id }))}
                            columns={columns}
                            filterBy={[]}
                            isActive={false}
                        />
                    )}
                </Space>
            </Card>

            <ItemForm
                visible={isModalVisible}
                onCreate={handleCreate}
                onCancel={() => {
                    setIsModalVisible(false);
                    setSelectedRecord(null);
                }}
                record={selectedRecord}
            />
        </div>
    );
};

export default Items;